import React, { useState, useEffect } from "react";
import { LOGO_HORIZONTAL } from "../data/images";
import { SHOW_PORTAL } from "../data/config";

// ── NAVIGATION LINKS ──────────────────────────────────────────
// Edit this array to add, remove, or rename navigation items
const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About Us", href: "#about" },
  {
    label: "Programs",
    href: "#programs",
    // Dropdown items — comment out to remove dropdown
    dropdown: [
      { label: "GradSHPE", href: "#programs", id: "gradshpe" },
      { label: "SHPEtinas", href: "#programs", id: "shpetinas" },
      { label: "Outreach", href: "#programs", id: "outreach" },
      { label: "MentorSHPE", href: "#programs", id: "mentorshpe" },
    ],
  },
  { label: "Events", href: "#events" },
  { label: "E-Board", href: "#eboard" },
  { label: "Newsletter", href: "#newsletter" },
  // Only shown when SHOW_PORTAL is true in src/data/config.js
  ...(SHOW_PORTAL ? [{ label: "Members", href: "#members" }] : []),
];

export default function Navbar({ currentUser, onLogin, onLogout }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: scrolled
          ? "rgba(255,255,255,0.97)"
          : "rgba(255,255,255,0.95)",
        backdropFilter: "blur(12px)",
        boxShadow: scrolled
          ? "0 2px 20px rgba(0,0,0,0.12)"
          : "0 1px 0 rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 70,
        }}
      >
        {/* LOGO — replace LOGO_HORIZONTAL in src/data/images.js */}
        <a
          href="#home"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <img
            src={LOGO_HORIZONTAL}
            alt="SHPE@VT Logo"
            style={{ height: 40, objectFit: "contain" }}
            onError={(e) => {
              // Fallback if logo fails to load
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <span
            style={{
              display: "none",
              fontFamily: "Playfair Display, serif",
              fontWeight: 900,
              fontSize: 20,
              color: "#861F41",
            }}
          >
            SHPE<span style={{ color: "#E5751F" }}>@VT</span>
          </span>
        </a>

        {/* DESKTOP NAV */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 4 }}
          className="hide-mobile"
        >
          {NAV_LINKS.map((link) => (
            <div
              key={link.label}
              style={{ position: "relative" }}
              onMouseEnter={() => link.dropdown && setOpenDropdown(link.label)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <a
                href={link.href}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#333",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#F5F0F0")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {link.label}
                {link.dropdown && <span style={{ fontSize: 10 }}>▾</span>}
              </a>
              {link.dropdown && openDropdown === link.label && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    minWidth: 160,
                    background: "white",
                    borderRadius: 10,
                    padding: "8px 0",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                    animation: "fadeIn 0.15s ease",
                  }}
                >
                  {link.dropdown.map((sub) => (
                    <a
                      key={sub.label}
                      href={sub.href}
                      onClick={(e) => {
                        if (sub.id) {
                          e.preventDefault();
                          window.dispatchEvent(
                            new CustomEvent("switchProgram", {
                              detail: sub.id,
                            }),
                          );
                          document
                            .getElementById("programs")
                            ?.scrollIntoView({ behavior: "smooth" });
                          setOpenDropdown(null);
                        }
                      }}
                      style={{
                        display: "block",
                        padding: "10px 18px",
                        fontSize: 14,
                        color: "#333",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#F5F0F0")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      {sub.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* RIGHT SIDE ACTIONS */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Contact Us — replaces the Members Portal button; links to the
              "Get in Touch" section lower on the page. UPDATE the href below
              if that section's id isn't #contact. */}
          <a
            href="#contact"
            className="btn btn-members"
            style={{ fontSize: 13, padding: "9px 16px" }}
          >
            Contact Us
          </a>

          {/* JOIN SHPE — links to SHPE National's membership/registration
              page (shpe.org/membership), not MEMBERSHIP.joinUrl, since that
              one (HokieSync) isn't working yet. UPDATE: swap back to
              {MEMBERSHIP.joinUrl} (re-add the import) once that's fixed. */}
          <a
            href="https://shpe.org/membership"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary hide-mobile"
            style={{ fontSize: 13, padding: "9px 18px" }}
          >
            Join SHPE
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ display: "none", padding: 8, borderRadius: 8 }}
            className="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            <div
              style={{
                width: 22,
                height: 2,
                background: "#333",
                marginBottom: 5,
                transition: "all 0.3s",
                transform: menuOpen ? "rotate(45deg) translateY(7px)" : "none",
              }}
            />
            <div
              style={{
                width: 22,
                height: 2,
                background: "#333",
                marginBottom: 5,
                opacity: menuOpen ? 0 : 1,
                transition: "opacity 0.3s",
              }}
            />
            <div
              style={{
                width: 22,
                height: 2,
                background: "#333",
                transition: "all 0.3s",
                transform: menuOpen
                  ? "rotate(-45deg) translateY(-7px)"
                  : "none",
              }}
            />
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div
          style={{
            background: "white",
            borderTop: "1px solid #eee",
            padding: "16px 24px",
            animation: "fadeInUp 0.2s ease",
          }}
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              style={{
                display: "block",
                padding: "12px 0",
                borderBottom: "1px solid #f0f0f0",
                fontSize: 15,
                fontWeight: 500,
                color: "#333",
              }}
            >
              {link.label}
            </a>
          ))}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <a
              href="https://shpe.org/membership"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="btn btn-primary"
              style={{ flex: 1, justifyContent: "center" }}
            >
              Join SHPE
            </a>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </nav>
  );
}
